import React, {useMemo, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import type {ReportsStackParamList} from '@/app/routes';
import {
  exportPersonAttendance,
  fetchPersonAttendance,
} from '@/entities/attendance/api';
import {
  currentMonth,
  periodKey,
  periodParam,
  type Period,
} from '@/entities/attendance/period';
import {PersonAvatar} from '@/entities/personnel/ui/PersonAvatar';
import {useAuthStore} from '@/entities/session/auth-store';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';
import {reportFileName} from '@/features/view-attendance/lib/csv';
import {TimesheetPicker} from '@/features/export-timesheet/ui/TimesheetPicker';
import {ExportBar} from '@/features/view-attendance/ui/ExportBar';
import {MonthCalendar} from '@/features/view-attendance/ui/MonthCalendar';
import {ReportToolbar} from '@/features/view-attendance/ui/ReportToolbar';
import {PersonDayList} from '@/features/view-attendance/ui/PersonDayList';
import {ScheduleSummary} from '@/features/view-attendance/ui/ScheduleSummary';
import {AttendanceKpi} from '@/features/view-attendance/ui/AttendanceKpi';
import {
  describePeriod,
  usePeriodLabel,
} from '@/features/view-attendance/lib/period-label';
import {buildCsv} from '@/features/view-attendance/lib/csv';
import {useParamState} from '@/shared/lib/use-param-state';
import {AppButton} from '@/shared/ui/button';
import {Disclosure} from '@/shared/ui/disclosure';
import {EmptyState} from '@/shared/ui/empty-state';
import {Explain, type Definition} from '@/shared/ui/explain';
import {Page} from '@/shared/ui/page';
import {Section} from '@/shared/ui/section';
import {Skeleton} from '@/shared/ui/skeleton';
import {formatClock} from '@/shared/lib/format';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * One person for one period — the bottom rung of the ladder, and the only view that
 * shows the individual in→out episodes a day is made of (see PersonDayList for why
 * both durations are stated) and, under them, that day's raw access journal: every
 * event the terminals recorded for this person, denials included, which the episodes
 * above cannot contain.
 *
 * Reached from either of the two rungs above it, always carrying the period they were
 * showing. The employer-side route is used here (`/control/personnel/{id}/attendance`);
 * the self-service twin an employee opens on their own attendance tab is
 * `/me/attendance`, a different route with a different authorization story, and the two
 * must not be swapped for one another.
 */
type Props = NativeStackScreenProps<ReportsStackParamList, 'EmployeeReport'>;

/**
 * A clock value for a spreadsheet cell: empty rather than the em dash the UI shows.
 * A dash in a time column is a value a spreadsheet will happily sort and filter on.
 */
function csvClock(millis: number | null | undefined): string {
  return millis == null ? '' : formatClock(millis);
}

const styles = StyleSheet.create({
  loading: {gap: spacing(3)},
});

export function EmployeeReportScreen({navigation, route}: Props) {
  const {t, i18n} = useTranslation();
  const {label: periodTitle} = usePeriodLabel();
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  const {recordId, name} = route.params;

  /**
   * Журнал — своя вкладка, а этот экран живёт в «Посещаемости», поэтому переход идёт
   * через `getParent()`: у вкладочного навигатора нет статически известного списка
   * параметров (вкладки собираются в рантайме по персоне — `tabsFor`), и это одно из
   * немногих мест, где кабинет пересекает границу вкладок по имени. Родителя может не
   * быть — тогда тап просто ничего не делает, а не роняет экран.
   */
  const openEvents = (scopePeriod: Period) => {
    const parent = navigation.getParent() as unknown as
      | {navigate: (route: string, params?: object) => void}
      | undefined;
    parent?.navigate('Journal', {
      screen: 'Journal',
      params: {recordId, name: title, period: periodKey(scopePeriod)},
    });
  };

  const now = useMemo(() => new Date(), []);
  /*
    Период живёт В АДРЕСЕ, как и на «Посещаемости», откуда сюда приходят: здесь его
    меняют переключателем, им делятся и в него возвращаются кнопкой «назад». Параметр
    маршрута — тот же компактный ключ, а не объект `Period` (см. `ReportsStackParamList`),
    поэтому разбирает его кодек периода; см. `shared/lib/use-param-state`.

    Кодек БЕЗ умолчания: отсюда период из адреса не исчезает никогда — отчёт по
    сотруднику всегда за какой-то период, и по ссылке обязан открыться тот же. Значение
    ниже нужно ровно для голой ссылки без параметра.
  */
  const thisMonth = useMemo(() => currentMonth(now), [now]);
  const periodCodec = useMemo(() => periodParam(now), [now]);
  const [period, setPeriod] = useParamState<Period>('period', thisMonth, periodCodec);
  // The row-by-row list is the audit trail behind the calendar, not the way in.
  const [listOpen, setListOpen] = useState(false);

  const report = useQuery({
    queryKey: ['attendance-person', companyId, recordId, periodKey(period)],
    queryFn: () => fetchPersonAttendance(companyId as string, recordId, period),
    enabled: !!companyId,
  });

  const days = report.data?.days ?? [];

  if (!companyId) {
    return <ContextPlaceholder titleKey="nav.reports" />;
  }

  const person = report.data?.personnel;
  // `name` приходит из параметров перехода, а в URL его нет: `/reports/employee/:recordId`
  // — по чужой ссылке заголовок пуст, пока не приедет отчёт. Пустая строка, а не
  // `undefined`: её умеют и заголовок, и аватар.
  const title = person?.full_name ?? name ?? '';
  const periodLabel = periodTitle(report.data ?? describePeriod(period));
  const fileStem = reportFileName(
    [t('reports.fileAttendance'), title, periodLabel],
    '',
  ).slice(0, -1);

  /** One row per day — the person's own detail, which has no cross-person columns. */
  const localCsv = () =>
    buildCsv(
      [
        t('reports.csv.date'),
        t('reports.csv.weekday'),
        t('reports.csv.scheduled'),
        t('reports.csv.arrival'),
        t('reports.csv.departure'),
        t('reports.csv.arrivalDevice'),
        t('reports.csv.departureDevice'),
        t('reports.csv.events'),
        t('reports.csv.workedMinutes'),
        t('reports.csv.intervalMinutes'),
        t('reports.csv.flags'),
      ],
      days.map(day => [
        day.date,
        t(`attendance.weekday.${day.weekday}`),
        day.scheduled ? 1 : 0,
        csvClock(day.arrival_time_millis),
        csvClock(day.departure_time_millis),
        day.arrival_device_name ?? '',
        day.departure_device_name ?? '',
        day.event_count,
        day.worked_minutes ?? '',
        day.interval_minutes ?? '',
        day.flags.join(' '),
      ]),
    );

  /**
   * The two durations are the reason this screen exists, and they are the reason a
   * payroll conversation goes wrong: one includes the lunch break and the other does
   * not. Both are on screen; this says which is which.
   */
  const dayDefinitions: Definition[] = [
    'spanWithBreaks',
    'spanInside',
    'events',
    'intervals',
  ].map(id => ({
    term: t(`reports.def.${id}.term`),
    meaning: t(`reports.def.${id}.meaning`),
  }));

  let content: React.ReactNode;

  if (report.isPending) {
    content = (
      <View style={styles.loading}>
        <Skeleton height={190} radius={radii.xl} />
        <Skeleton height={140} radius={radii.xl} />
      </View>
    );
  } else if (report.isError) {
    content = (
      <EmptyState
        tone="error"
        title={t('reports.error')}
        actionLabel={t('actions.retry')}
        onAction={() => void report.refetch()}
      />
    );
  } else {
    content = (
      <>
        <Section>
          {/* Те же плитки, что на «Посещаемости» и в отчёте по отделу. Разложения по
              людям здесь нет и быть не может — отчёт об одном человеке, — поэтому
              «Сотрудников» и «Ни разу не отметились» не рисуются, а «Пропусков» и
              «Опозданий» называют знаменателем дни, а не людей. */}
          <AttendanceKpi summary={report.data.summary} />
        </Section>
        <Section>
        <ScheduleSummary
          schedule={
            report.data.schedule ??
            person?.effective_attendance_schedule ??
            person?.attendance_schedule ??
            null
          }
          source={
            report.data.schedule_source ??
            person?.effective_attendance_schedule_source ??
            null
          }
        />
        </Section>
        {days.length === 0 ? (
          <EmptyState title={t('reports.noData')} />
        ) : (
          <>
            {/* The month as a shape, not as thirty rows — see MonthCalendar. The
                per-day BAR chart that used to sit here plotted one person, so every
                column was a bar of height one: a picture of nothing. */}
            <Section
              title={t('reports.byDay')}
              hint={t('reports.calendarSubtitle')}
              aside={
                <Explain
                  testID="explain-days"
                  title={t('reports.byDay')}
                  subtitle={t('reports.byDayExplainHint')}
                  definitions={dayDefinitions}
                />
              }>
              <MonthCalendar
                days={days}
                companyId={companyId}
                recordId={recordId}
                onOpenArchive={date =>
                  openEvents({kind: 'range', from: date, to: date})
                }
              />
            </Section>

            <Section>
            <Disclosure
              testID="section-day-list"
              title={t('reports.dayList')}
              summary={t('reports.dayListHint', {count: days.length})}
              open={listOpen}
              onToggle={() => setListOpen(v => !v)}>
              <PersonDayList
                days={days}
                companyId={companyId}
                recordId={recordId}
                onOpenArchive={date =>
                  openEvents({kind: 'range', from: date, to: date})
                }
              />
            </Disclosure>
            </Section>
          </>
        )}
      </>
    );
  }

  return (
    <Page
      scroll
      title={title}
      /* Лицо, а не глиф раздела: этот экран — не «Посещаемость», а конкретный
         человек, и опознают его именно так, ещё до того как прочтут фамилию. */
      mark={
        <PersonAvatar
          companyId={companyId}
          recordId={recordId}
          revision={person?.revision}
          name={title}
          size={40}
        />
      }
      subtitle={
        [person?.position, person?.department_names?.join(', '), periodLabel || null]
          .filter(Boolean)
          .join('  \u00b7  ') || t('reports.employeeSubtitle')
      }
      refreshing={report.isRefetching}
      onRefresh={() => void report.refetch()}
      testID="employee-report"
      actions={
        /* Весь журнал этого человека за период отчёта — там же, где он выгружается:
           события принимают `record_id` и как фильтр, и как фильтр выгрузки. */
        <AppButton
          title={t('reports.personLog')}
          tone="outline"
          size="sm"
          icon="logs"
          onPress={() => openEvents(period)}
          testID="open-person-log"
        />
      }
      toolbar={
        <ReportToolbar
          period={period}
          onPeriodChange={setPeriod}
          now={now}
          periodLabel={periodLabel}
          exportSlot={
            <ExportBar
              fileName={fileStem}
              contents={t('reports.exportPerson', {
                period: periodLabel,
                count: days.length,
              })}
              onServer={format =>
                exportPersonAttendance(companyId, recordId, period, format, i18n.language)
              }
              onLocal={localCsv}
            />
          }
          /* Табель на одного человека: `recordIds` сужает набор до него, а колонки
             остаются те же, что у отдела, — тот же лист печатают и подшивают. */
          timesheetSlot={
            <TimesheetPicker
              companyId={companyId}
              period={period}
              periodLabel={periodLabel}
              recordIds={[recordId]}
              fileName={reportFileName(
                [t('timesheet.title'), title, periodLabel],
                '',
              ).slice(0, -1)}
              onCsv={() =>
                exportPersonAttendance(companyId, recordId, period, 'csv', i18n.language)
              }
            />
          }
        />
      }>
      {content}
    </Page>
  );
}
